import { Box, Button, Flex, Heading, HStack, Input, Text, VStack } from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Copy, Eye, Link as LinkIcon, Trash2, XIcon } from 'lucide-react'
import { useState } from 'react'
import { type ShareLinkResponse, savedPlaygroundsApi } from '@/lib/api'
import { toaster } from '~/toaster'

interface SharePlaygroundDialogProps {
  playgroundId: string
  playgroundName: string
  onClose: () => void
}

function SharePlaygroundDialog({
  playgroundId,
  playgroundName,
  onClose,
}: SharePlaygroundDialogProps) {
  const queryClient = useQueryClient()
  const [copied, setCopied] = useState(false)
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false)

  // Fetch existing share link
  const {
    data: shareLink,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['share-link', playgroundId],
    queryFn: () => savedPlaygroundsApi.getShareLink(playgroundId),
    retry: false,
  })

  // Create share link mutation
  const createMutation = useMutation({
    mutationFn: () => savedPlaygroundsApi.createShareLink(playgroundId),
    onSuccess: (data) => {
      queryClient.setQueryData(['share-link', playgroundId], data)
      toaster.create({
        title: 'Share link created',
        description: 'Anyone with this link can view your playground',
        type: 'success',
      })
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to create share link',
        description: error.message,
        type: 'error',
      })
    },
  })

  // Revoke share link mutation
  const revokeMutation = useMutation({
    mutationFn: () => savedPlaygroundsApi.revokeShareLink(playgroundId),
    onSuccess: () => {
      queryClient.setQueryData(['share-link', playgroundId], null)
      queryClient.invalidateQueries({ queryKey: ['share-link', playgroundId] })
      setShowRevokeConfirm(false)
      toaster.create({
        title: 'Share link revoked',
        description: 'The share link is no longer active',
        type: 'success',
      })
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to revoke share link',
        description: error.message,
        type: 'error',
      })
    },
  })

  const handleCopyLink = async () => {
    if (shareLink?.shareUrl) {
      await navigator.clipboard.writeText(shareLink.shareUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      toaster.create({
        title: 'Link copied',
        description: 'Share link copied to clipboard',
        type: 'success',
      })
    }
  }

  const hasShareLink = shareLink && !error
  const isPending = createMutation.isPending || revokeMutation.isPending

  return (
    <Box
      position="fixed"
      inset={0}
      bg="blackAlpha.600"
      zIndex={2000}
      onClick={onClose}
      overflow="auto"
    >
      <Box
        position="relative"
        mx="auto"
        my={8}
        bg="bg.panel"
        borderRadius="lg"
        w="90vw"
        maxW="500px"
        overflow="hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <Flex justify="space-between" align="center" p={4} borderBottomWidth="1px">
          <HStack gap={2}>
            <LinkIcon size={20} />
            <Heading size="md">Share Playground</Heading>
          </HStack>
          <Button size="sm" variant="ghost" onClick={onClose} disabled={isPending}>
            <XIcon size={16} />
          </Button>
        </Flex>

        <Box p={6}>
          <VStack gap={4} align="stretch">
            {/* Playground name */}
            <Box bg="bg.subtle" p={3} borderRadius="md">
              <Text fontSize="sm" color="content.secondary">
                Sharing: <strong>{playgroundName}</strong>
              </Text>
            </Box>

            {isLoading ? (
              <Text color="content.secondary" textAlign="center" py={4}>
                Loading share link...
              </Text>
            ) : hasShareLink ? (
              <>
                {/* Share link info */}
                <Box>
                  <Text fontSize="sm" color="content.secondary" mb={2}>
                    Anyone with this link can view this playground without signing in.
                  </Text>
                  <HStack>
                    <Input value={shareLink.shareUrl} readOnly size="sm" bg="bg.subtle" />
                    <Button size="sm" variant="outline" onClick={handleCopyLink} minW="80px">
                      {copied ? <Check size={16} /> : <Copy size={16} />}
                      {copied ? 'Copied' : 'Copy'}
                    </Button>
                  </HStack>
                </Box>

                {/* View count */}
                <HStack color="content.secondary" fontSize="sm">
                  <Eye size={16} />
                  <Text>
                    Viewed {shareLink.viewCount} {shareLink.viewCount === 1 ? 'time' : 'times'}
                  </Text>
                </HStack>

                {/* Revoke section */}
                {showRevokeConfirm ? (
                  <Box bg="red.50" _dark={{ bg: 'red.900' }} p={4} borderRadius="md">
                    <Text fontSize="sm" mb={3}>
                      Are you sure you want to revoke this share link? Anyone with the link will no
                      longer be able to view this playground.
                    </Text>
                    <HStack justify="flex-end" gap={2}>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setShowRevokeConfirm(false)}
                        disabled={revokeMutation.isPending}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        colorPalette="red"
                        onClick={() => revokeMutation.mutate()}
                        loading={revokeMutation.isPending}
                      >
                        Revoke Link
                      </Button>
                    </HStack>
                  </Box>
                ) : (
                  <Button
                    variant="ghost"
                    colorPalette="red"
                    size="sm"
                    onClick={() => setShowRevokeConfirm(true)}
                  >
                    <Trash2 size={16} />
                    Revoke share link
                  </Button>
                )}
              </>
            ) : (
              <>
                {/* No share link yet */}
                <Box textAlign="center" py={4}>
                  <Text color="content.secondary" mb={4}>
                    Create a share link to allow anyone to view this playground without signing in.
                  </Text>
                  <Button
                    colorPalette="brand"
                    onClick={() => createMutation.mutate()}
                    loading={createMutation.isPending}
                  >
                    <LinkIcon size={16} />
                    Create Share Link
                  </Button>
                </Box>
              </>
            )}

            {/* Close button */}
            <HStack justify="flex-end" pt={2}>
              <Button variant="ghost" onClick={onClose} disabled={isPending}>
                Close
              </Button>
            </HStack>
          </VStack>
        </Box>
      </Box>
    </Box>
  )
}

export default SharePlaygroundDialog
